import * as Sentry from "@sentry/react"
import { invalidateAllQueries } from "bonzai"
import { type ResultOf } from "graphql-mock"
import { graphql, graphqlRequest } from "../../graphql"
import { dispatchConfettiForProgressChange } from "./ConfettiContextProvider"

/**
 * Bounded poll bounds. The backend scores playthroughs asynchronously, so on
 * completion we poll for the processed result instead of listening on a
 * WebSocket. The poll stops the instant a change is detected, so a wider cap
 * only costs extra polls when a completion is genuinely slow to process — the
 * 20s cap gives prod headroom over staging's (faster) scoring latency.
 */
const POLL_INTERVAL_MS = 1500
const POLL_TIMEOUT_MS = 20000

const query = graphql(`
  query CurrentProductProgress($productId: ID!) {
    product(id: $productId) {
      progress {
        certified
        highScore
        starsCount
      }
    }
  }
`)

type Data = ResultOf<typeof query>
export type ProductProgress = Data["product"]["progress"]

export type ProgressFetcher = (
  productId: string
) => Promise<ProductProgress | undefined>

/** Before-snapshot per product, seeded on open (no extra network call). */
const productProgressMap = new Map<string, ProductProgress>()

/** Products with an in-flight poll — single-flight guard. */
const activePolls = new Set<string>()

/**
 * Store the product's progress at open time so completion can diff against it.
 * No network call — the host already has this progress in its query.
 */
export const snapshotProgressBefore = (
  productId: string,
  progress: ProductProgress
) => {
  productProgressMap.set(productId, progress)
}

/**
 * Pure diff: did anything the confetti/refresh cares about change? This decides
 * only *whether* to invalidate. The confetti-TYPE decision (certified vs
 * high-score) stays solely in `dispatchConfettiForProgressChange`.
 */
export const hasProgressChanged = (
  before: ProductProgress | undefined,
  after: ProductProgress | undefined
): boolean => {
  if (before === undefined || after === undefined) return false

  return (
    before.certified !== after.certified ||
    before.highScore !== after.highScore ||
    before.starsCount !== after.starsCount
  )
}

const fetchProgress: ProgressFetcher = async (productId) => {
  try {
    const result = await graphqlRequest({ query, variables: { productId } })
    return result.product.progress
  } catch (error) {
    console.error("Error on query product progress: ", error)
    Sentry.setTag("feature", "queryCurrentProductProgress")
    Sentry.captureException(error)
    return undefined
  }
}

/**
 * On completion, poll the backend for the (async) processed progress. When a
 * change is detected we refresh all queries and hand the before/after to the
 * existing confetti/diploma path. Bounded (stops at POLL_TIMEOUT_MS) and
 * single-flight (a second call for a product already polling is a no-op).
 *
 * `fetcher` is an injectable timing seam so the bounded loop can be tested with
 * fake timers without a mocked graphql endpoint.
 * ponytail: injected fetcher = timing test seam, not an abstraction layer
 */
export const pollForProgressChange = (
  productId: string,
  fetcher: ProgressFetcher = fetchProgress
) => {
  if (activePolls.has(productId)) return

  const before = productProgressMap.get(productId)
  // No before-snapshot (e.g. close during the loading state before the query
  // resolved) means the diff can never be true — polling would just fire ~7
  // requests over the full 10s window for nothing. Skip it.
  if (before === undefined) return

  activePolls.add(productId)

  const startTime = Date.now()

  const stop = () => {
    activePolls.delete(productId)
  }

  const tick = async () => {
    try {
      const after = await fetcher(productId)

      if (hasProgressChanged(before, after) && after) {
        productProgressMap.set(productId, after)
        invalidateAllQueries()
        dispatchConfettiForProgressChange({
          productId,
          before: { certified: before.certified, highScore: before.highScore },
          after: { certified: after.certified, highScore: after.highScore },
        })
        stop()
        return
      }

      if (Date.now() - startTime >= POLL_TIMEOUT_MS) {
        stop()
        return
      }

      setTimeout(() => void tick(), POLL_INTERVAL_MS)
    } catch (error) {
      // A throw from invalidate/dispatch (or a rejected fetcher) must still
      // release the single-flight entry, or this product can never poll again.
      Sentry.captureException(error)
      stop()
    }
  }

  setTimeout(() => void tick(), POLL_INTERVAL_MS)
}
